import React, { useMemo, useState } from 'react';
import type { Language, ShoppingCart } from '../types';
import XIcon from './icons/XIcon';
import SearchField from './SearchField';
import { getTranslation } from '../utils/translations';
import {
    ALL_PILLARS,
    buildListPillars,
    getPillarDisplayName,
} from '../utils/shoppingCategories';

interface ChangeCategoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (pillarId: string) => void;
    shoppingCarts?: ShoppingCart[];
    itemCount: number;
    language?: Language;
}

const ChangeCategoryModal: React.FC<ChangeCategoryModalProps> = ({
    isOpen,
    onClose,
    onSelect,
    shoppingCarts = [],
    itemCount,
    language,
}) => {
    const t = (key: string) => getTranslation(language, key);
    const [query, setQuery] = useState('');

    const listPillars = useMemo(() => buildListPillars(shoppingCarts), [shoppingCarts]);

    const builtInOptions = useMemo(
        () => ALL_PILLARS.filter((p) => p !== 'others' && p !== 'manual'),
        [],
    );

    const filteredBuiltIn = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return builtInOptions;
        return builtInOptions.filter((pillarId) =>
            getPillarDisplayName(pillarId, listPillars, t).toLowerCase().includes(q),
        );
    }, [builtInOptions, listPillars, query, t]);

    const filteredLists = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return listPillars;
        return listPillars.filter((pillar) => pillar.name.toLowerCase().includes(q));
    }, [listPillars, query]);

    if (!isOpen) return null;

    const handleSelect = (pillarId: string) => {
        onSelect(pillarId);
        setQuery('');
    };

    const handleClose = () => {
        setQuery('');
        onClose();
    };

    return (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[510] flex items-center justify-center p-4 animate-fade-in"
            onClick={handleClose}
        >
            <div
                className="bg-white dark:bg-slate-900 w-full max-w-md max-h-[min(85dvh,calc(100dvh-2rem))] rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden border border-white/20 dark:border-slate-800 transition-colors"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-5 sm:p-6 border-b border-slate-100 dark:border-slate-800 shrink-0">
                    <div className="flex items-start justify-between gap-3 mb-4">
                        <div className="min-w-0">
                            <h3 className="text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight">
                                {t('changeCategory')}
                            </h3>
                            <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-1">
                                {itemCount} {t('itemsLabel')}
                            </p>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shrink-0"
                            aria-label={t('close')}
                        >
                            <XIcon className="w-5 h-5" />
                        </button>
                    </div>

                    <SearchField
                        value={query}
                        onChange={setQuery}
                        placeholder={t('search')}
                        clearLabel={t('clearSearch')}
                        inputClassName="font-medium focus:ring-brand-sage/30"
                    />
                </div>

                <div className="flex-grow overflow-y-auto p-4 sm:p-5 space-y-5 scrollbar-none">
                    {filteredBuiltIn.length > 0 && (
                        <section>
                            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500 mb-2 px-1">
                                {t('builtInCategories')}
                            </h4>
                            <div className="space-y-1.5">
                                {filteredBuiltIn.map((pillarId) => (
                                    <button
                                        key={pillarId}
                                        onClick={() => handleSelect(pillarId)}
                                        className="w-full text-start px-4 py-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/80 hover:bg-brand-sage/10 dark:hover:bg-brand-sage/20 border border-slate-100 dark:border-slate-700 hover:border-brand-sage/30 text-sm font-bold text-slate-700 dark:text-slate-200 transition-all active:scale-[0.99]"
                                    >
                                        {getPillarDisplayName(pillarId, listPillars, t)}
                                    </button>
                                ))}
                            </div>
                        </section>
                    )}

                    {filteredLists.length > 0 && (
                        <section>
                            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500 mb-2 px-1">
                                {t('yourShoppingLists')}
                            </h4>
                            <div className="space-y-1.5">
                                {filteredLists.map((pillar) => (
                                    <button
                                        key={pillar.id}
                                        onClick={() => handleSelect(pillar.id)}
                                        className="w-full text-start px-4 py-3.5 rounded-2xl bg-sky-50/80 dark:bg-sky-900/20 hover:bg-sky-100 dark:hover:bg-sky-900/40 border border-sky-100 dark:border-sky-800 hover:border-sky-300 dark:hover:border-sky-600 text-sm font-bold text-sky-800 dark:text-sky-200 transition-all active:scale-[0.99]"
                                    >
                                        {pillar.name}
                                    </button>
                                ))}
                            </div>
                        </section>
                    )}

                    {filteredBuiltIn.length === 0 && filteredLists.length === 0 && (
                        <p className="text-center text-sm font-medium text-slate-400 dark:text-slate-500 py-8">
                            {t('noResults')}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChangeCategoryModal;
